import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official'; // Or use @highcharts/react components
import 'highcharts/modules/exporting';
import { formatNumber } from '../../../../Common/FormatNumber';

interface ChartProps {
  id: number;
  title: string;
  height: number;
}

const LowMargin: React.FC<ChartProps> = ({ id, title, height = 345 }) => {
  const [lowMargin, setLowMargin] = React.useState([]);
  const chartComponentRef = React.useRef<HighchartsReact.RefObject>(null);
  React.useEffect(() => {
    const fetchData = async () => {
        try {
          const response = await fetch(`/dashboard/low-margin/${id}/?entity_id=${localStorage.getItem('entity_id')}`);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data: any[] = await response.json();
          setLowMargin(data['margins']);
        } catch (err) {
            console.log(err);     
        } finally {}
    };

    fetchData();

    const handleResize = () => {
      if (chartComponentRef.current && chartComponentRef.current.chart) {
        chartComponentRef.current.chart.reflow();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const options: Highcharts.Options = {
        chart: {
            type: 'scatter',
            zooming: {
                type: 'xy' as const
            }
        },
        lang: {
            thousandsSep: ','
        },
        title: {
            text: title,
            align: 'left'
        },
        
        yAxis: {
            title: {
                text: 'Gross Profit (%)',
                style: {
                    fontFamily: 'Trebuchet MS'
                }
            },
            startOnTick: true,
            endOnTick: true,
            showLastLabel: true,
            min: 0,
            max: 100,
        },
        xAxis: {
            title: {
                text: 'Sales ($)',
                style: {
                    fontFamily: 'Trebuchet MS'
                }
            },
            labels: {
                formatter: function(this: Highcharts.AxisLabelsFormatterContextObject) {
                    const v = this.value as number;
                    const absv = Math.abs(v);
                    if (absv >= 1000000) return v / 1000000 + 'M';
                    if (absv >= 1000) return v / 1000 + 'K';
                    return String(v);
                },
                style: {
                    fontFamily: 'Trebuchet MS'
                }
            },
            startOnTick: true,
            endOnTick: true,
            showLastLabel: true,
            min: 0
        },
        
        exporting: {
            enabled: true, // Enable the export menu
            buttons: {
                contextButton: {
                    menuItems: [
                        'downloadPNG',
                        'downloadJPEG',
                        'downloadPDF',
                        'downloadSVG',
                        'separator',
                        'printChart',
                    ],
                },
            },
        },

        plotOptions: {
            scatter: {
                marker: {
                    radius: 2.5,
                    symbol: 'circle',
                    states: {
                        hover: {
                            enabled: true,
                            lineColor: 'rgb(100,100,100)'
                        }
                    }
                },
                pointInterval: 1000,
                states: {
                    hover: {
                        enabled: false
                    }
                },
                jitter: {
                    x: 0.005
                }
            }
        },
        tooltip: {
            stickOnContact: true,
            hideDelay: 99999999,
            useHTML: true,
            style: {
              pointerEvents: 'auto'
            },
            formatter: function(this: Highcharts.Point) {
                // Safely access id and series properties
                const id = (this as any).id ?? '';
                const name = (this as any).name ?? '';
                const y = (this as any).y ?? '';
                const x = (this as any).x ?? '';
                const invoice_date = (this as any).date ?? '';
                const items = (this as any).items ?? '';
                // For series, get the id or name if available
                let customerId = '';
                if (this.series && (this.series as any).userOptions && (this.series as any).userOptions.id) {
                    customerId = (this.series as any).userOptions.id;
                } else if (id) {
                    customerId = id;
                } else {
                    customerId = '';
                }
                return `<span style="font-family: Trebuchet MS;">Invoice #: <b>${name}</b><br>Invoice Date: ${invoice_date}<br/>Sales: ${formatNumber(x, 'en-US', 'USD')}<br>Gross Profit (%): ${y.toFixed(1)}<br>Number of Items: ${items}</span>`
            }
            // pointFormat: 'Name: <b>{point.name}</b><br>Share of Stock Orders: {point.y}<br>Sales: ${point.x:.2f}<br/><a href="/customers/{point.id}/">View Customer</a>'
        },
        series: [
            {
                name: 'Low Margin',
                type: 'scatter',
                showInLegend: false,
                data: lowMargin
            }
        ]
    };

    return (
        <div style={{ height: '100%', width: '100%' }}>
            <HighchartsReact
                highcharts={Highcharts}
                options={options}
                allowChartUpdate={true}
                exportEnabled={true}
                ref={chartComponentRef}
                containerProps={{ style: { width: '100% !important', height: height, borderRadius: 5, boxShadow: 'none', margin: 0, padding: 0 } }}
            />
        </div>
    );
};

export default LowMargin;
